import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import type { PlanSummary } from "@edu/shared";
import * as api from "../../lib/api.ts";
import { planCountText } from "../../lib/plurals.ts";
import { filterByQuery } from "../../lib/search.ts";
import { NothingFound, SearchField } from "../../lib/SearchField.tsx";
import { Empty } from "../../lib/Empty.tsx";
import { LoadingRows } from "../../lib/loading.tsx";
import { NameDialog } from "../shell/NameDialog.tsx";
import { PlanRow, PlanTrashRow } from "./PlanRow.tsx";

/**
 * Заготовки уроков: список кабинета.
 *
 * Заготовка это материал, а не занятие: к ученику она не привязана, правится
 * когда угодно, и по одной и той же заготовке проводятся уроки с разными
 * людьми. Урок при этом получает КОПИЮ слайдов, поэтому правка заготовки
 * прошлые занятия не трогает.
 *
 * Экран лежит в стартовом чанке, в отличие от материалов и редактора: здесь
 * нет ни Excalidraw, ни загрузчика, а лишний ленивый чанк на канале 128 кбит/с
 * это лишний круг по сети.
 *
 * Плоский список без папок: у первого преподавателя заготовок десятки, и
 * дерево здесь было бы решением задачи, которой пока нет.
 *
 * Строка живёт в `PlanRow.tsx`: у неё своё состояние (переименование, шаги
 * подтверждения), и экран без неё влезает в глаз целиком.
 *
 * ⚠️ **Заведение и поиск поменялись местами 14.09.2026** (подход 2е переноса
 * дизайн-системы). Форма из одного поля «Название заготовки» с кнопкой над
 * списком читалась как фильтр, а настоящего поиска не было ни на одном экране
 * продукта. Теперь место над списком занял поиск, а заведение стало главным
 * действием экрана и открывает окно.
 */
export function PlansPage() {
  const [plans, setPlans] = useState<PlanSummary[] | null>(null);
  const [trash, setTrash] = useState<PlanSummary[]>([]);
  const [query, setQuery] = useState("");
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  /**
   * Отказ заведения. ⚠️ Отдельно от `err` экрана: тот печатается строкой под
   * шапкой, то есть ЗА окном, и человек прочитал бы его только закрыв окно, из
   * которого отказ и приехал.
   */
  const [addErr, setAddErr] = useState<string | null>(null);

  // Часы берутся один раз на отрисовку, а не в каждой строке: иначе соседние
  // строки одного списка считают «сегодня» по разным моментам времени
  const now = Date.now();

  const reload = useCallback(async () => {
    try {
      const res = await api.listPlans();
      setPlans(res.plans);
      setTrash(res.trash);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const run = useCallback(
    async (fn: () => Promise<void>) => {
      setBusy(true);
      setErr(null);
      try {
        await fn();
        await reload();
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(false);
      }
    },
    [reload],
  );

  const create = (t: string) => {
    setBusy(true);
    setAddErr(null);
    api.createPlan(t).then(
      () => {
        setBusy(false);
        setAdding(false);
        /*
          ⚠️ Запрос снимается заведением: иначе созданная под набранным поиском
          заготовка не попала бы в отобранное, и экран ответил бы «ничего не
          нашлось» на только что сделанную работу.
        */
        setQuery("");
        void reload();
      },
      (e: unknown) => {
        setBusy(false);
        setAddErr(e instanceof Error ? e.message : String(e));
      },
    );
  };

  const all = plans ?? [];
  const shown = filterByQuery(all, query, (p) => p.title);

  return (
    <main className="home home--list">
      {/* Возврат в кабинет живёт в сайдбаре каркаса, здесь только название
          экрана: две кнопки «в кабинет» на одной странице это уже вопрос */}
      <header className="head">
        <div className="head__line">
          <h1 className="head__h">Заготовки</h1>
          {plans !== null && <span className="head__n">{planCountText(all.length)}</span>}
          {/* Единственный акцент экрана: заготовку тут заводят, всё остальное
              это действия над уже созданными строками */}
          <span className="head__act">
            <button type="button" className="btn btn--primary" onClick={() => setAdding(true)}>
              Новая заготовка
            </button>
          </span>
        </div>
        <div className="head__sub">
          Соберите слайды заранее, а потом создавайте по ним уроки с разными учениками. Урок
          получает копию слайдов: правка заготовки прошлые занятия не меняет
        </div>
      </header>

      {err && <p className="err">{err}</p>}

      {/* Полоса появляется вместе со списком: искать в том, чего ещё нет,
          нечем */}
      {plans !== null && all.length > 0 && (
        <div className="bar">
          <SearchField label="Поиск по названию" value={query} onChange={setQuery} />
          {query.trim() !== "" && (
            <span className="bar__n">
              {shown.length} из {all.length}
            </span>
          )}
        </div>
      )}

      <section className="card">
        {plans === null ? (
          <LoadingRows list="list--plans" cells={6} narrow="panel" />
        ) : all.length === 0 ? (
          /*
            Подзаголовок экрана уже объяснил, ЧТО такое заготовка. Чего из него
            не видно, так это откуда в слайде берётся картинка: файлы живут в
            отдельном разделе, и связь между двумя пунктами сайдбара из самих
            пунктов не выводится
          */
          <Empty
            icon="board"
            title="Заготовок пока нет"
            text={
              <>
                Слайды собираются на доске, а картинки, PDF и аудио для них берутся из{" "}
                <Link className="textlink" to="/media">
                  материалов
                </Link>
                .
              </>
            }
            action={{ label: "Новая заготовка", onClick: () => setAdding(true) }}
          />
        ) : shown.length === 0 ? (
          <NothingFound
            query={query}
            nothing="ни одной заготовки"
            onClear={() => setQuery("")}
          />
        ) : (
          /*
            ⚠️ **`data-rows="panel"`, а не `"page"`** (решение владельца
            17.09.2026). Ниже порога колонок строка перестаёт быть стопкой слотов
            с приписками и становится двумя строками, но ячейка действий при этом
            ОСТАЁТСЯ, и доводов к этому два. Первый: у `page` её прячут доводом
            «строка сама ведёт туда, где эти действия живут», а здесь имя
            заготовки не ссылка, оно правится на месте при переименовании, и
            вести строке некуда; в ячейке же стоит «Открыть», то есть
            единственный вход в заготовку, и спрятать его значит выключить
            строку. Второй: этот же разрез носит список корзины ниже, а там
            строка тем более пульт, единственное её действие это «Восстановить».
            Что именно уезжает и почему, расписано по слотам в `PlanRow.tsx`;
            сама раскладка живёт в `styles.css`, в блоке
            `@media (max-width: 63.99rem)`.
          */
          <ul className="list list--plans" data-rows="panel">
            <li className="list__head" aria-hidden="true">
              {/* Столбец шеврона: заголовка у него нет и быть не может */}
              <span />
              <span>Заготовка</span>
              <span>Слайдов</span>
              <span>Уроков</span>
              <span>Правка</span>
              <span>Действия</span>
            </li>
            {shown.map((p) => (
              <PlanRow key={p.id} plan={p} busy={busy} now={now} run={run} />
            ))}
          </ul>
        )}
      </section>

      {trash.length > 0 && (
        <section className="card">
          <h2>Корзина</h2>
          {/* Правило хранения словами один раз, а не в каждой строке: в строке
              стоит то, что от него осталось у этой заготовки */}
          <p className="muted">Хранится 30 суток с удаления, потом стирается.</p>
          {/* Метка та же, что у живого списка, и она тут верна дважды. По
              необходимости: разрез один на оба списка, а замок разрезов
              (`lib/list-cols.test.ts`) требует у разреза ровно одну метку. По
              существу: строка корзины это тоже пульт, действие в ней одно */}
          <ul className="list list--plans" data-rows="panel">
            <li className="list__head" aria-hidden="true">
              <span />
              <span>Заготовка</span>
              <span>Слайдов</span>
              <span>Уроков</span>
              <span>Уборка</span>
              <span>Действия</span>
            </li>
            {trash.map((p) => (
              <PlanTrashRow key={p.id} plan={p} busy={busy} now={now} run={run} />
            ))}
          </ul>
        </section>
      )}

      {adding && (
        <NameDialog
          title="Новая заготовка"
          label="Название заготовки"
          submit="Создать"
          maxLength={200}
          err={addErr}
          busy={busy}
          onSubmit={create}
          onClose={() => {
            setAdding(false);
            setAddErr(null);
          }}
        />
      )}
    </main>
  );
}
